/**
 * Math pure pour le shift gate (gating Conversations 2026-06-20).
 *
 * Calcule, à partir des blocs récurrents hebdomadaires d'un membre + un instant
 * `now` (epoch ms), le « windowing » courant :
 *   - dans quelle fenêtre on est (si on y est), avec scheduledStartMs / endMs
 *   - prochaine fenêtre à venir (si on n'y est pas)
 *
 * Convention bloc :
 *   weekdays  : entiers 1=Lun … 7=Dim, ≥1, uniques
 *   startMinute / endMinute : minutes depuis minuit (0–1439) DANS le fuseau
 *     d'ancrage du bloc (`timezone`)
 *   endMinute <= startMinute ⇒ overnight (bloc traverse minuit)
 *   timezone  : fuseau IANA d'ancrage du bloc, ou `null` → `FUSEAU_DES_SHIFTS`
 *
 * ⛔ **Le fuseau du poste n'entre nulle part ici** (10-09-2026). Chaque
 * occurrence est matérialisée en interprétant les minutes du bloc dans SON
 * fuseau d'ancrage, et le repli d'un bloc sans fuseau est la CONSTANTE de
 * l'agence — plus le device. La décision (dedans / dehors / prochaine) ET les
 * heures affichées sont donc les mêmes pour tout le monde, et les mêmes que
 * celles de Discord. Cf. `FUSEAU_DES_SHIFTS` pour ce que l'autre modèle a coûté.
 *
 * Toute la math reste PURE (pas d'effet de bord, pas de Date.now()) : `now` est
 * passé en paramètre — testable, déterministe.
 */

import {
  calendarDateInZone,
  formatTimeInZone,
  weekdayInZone,
  zonedDatePlusDays,
  zonedWallTimeToUtc,
} from "@/shared/timezone";
import { fuseauDuBloc } from "@/shared/shiftTime";

export interface ShiftBlock {
  _id: string;
  weekdays: number[];
  startMinute: number;
  endMinute: number;
  /** Fuseau IANA d'ancrage. `null` → `FUSEAU_DES_SHIFTS` (jamais le poste). */
  timezone: string | null;
  note: string | null;
  /** Lien vers le salon vocal que ce créneau exige (résolu par
   *  `discordShiftRoles`). `null` = aucun salon ne porte ces bornes, donc
   *  personne ne peut démarrer ce créneau. */
  discordVoiceUrl?: string | null;
}

export interface ShiftInstance {
  block: ShiftBlock;
  /** Epoch ms du début de cette occurrence (concrète, dans le calendrier). */
  startMs: number;
  /** Epoch ms de la fin de cette occurrence. */
  endMs: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Matérialise un wall-clock (date calendaire + minute du jour) dans `tz`. */
function wallMinuteToUtc(
  date: { year: number; month: number; day: number },
  minute: number,
  tz: string,
): number {
  return zonedWallTimeToUtc(
    {
      year: date.year,
      month: date.month,
      day: date.day,
      hour: Math.floor(minute / 60),
      minute: minute % 60,
    },
    tz,
  );
}

/**
 * Génère toutes les occurrences concrètes (startMs/endMs) des blocs sur une
 * fenêtre suffisamment large pour trouver « la fenêtre courante » et « la
 * prochaine ». On couvre la semaine passée (overnight commencé hier), la
 * semaine en cours et 2 semaines à venir = 4 semaines, ce qui suffit largement
 * (les chatters ont des shifts au moins hebdomadaires).
 *
 * Chaque occurrence est ANCRÉE dans le fuseau du bloc (`fuseauDuBloc`) : on
 * calcule, par bloc et par jour, la date calendaire DANS
 * ce fuseau qui contient `now` (puis décalée), et on convertit la minute du
 * bloc en epoch via `zonedWallTimeToUtc`. L'overnight calcule la fin sur la
 * date du LENDEMAIN dans le fuseau (wall-clock), pas `start + 24h` — correct si
 * une bascule DST tombe dans le créneau.
 *
 * Sort = tri par startMs croissant — pratique pour le caller.
 */
export function expandShiftInstances(
  shifts: ShiftBlock[],
  now: number,
): ShiftInstance[] {
  const out: ShiftInstance[] = [];

  for (const block of shifts) {
    const tz = fuseauDuBloc(block.timezone);
    // Jour de semaine (dans le fuseau d'ancrage) de la date contenant `now`.
    const anchorWeekday = weekdayInZone(now, tz);
    const overnight = block.endMinute <= block.startMinute;

    // -1 = semaine passée (capture overnight commencé hier soir, fini ce matin),
    // 0 = courante, 1/2 = à venir.
    for (let weekOffset = -1; weekOffset <= 2; weekOffset++) {
      for (const wd of block.weekdays) {
        // Décalage (en jours) de l'« aujourd'hui » du fuseau d'ancrage vers le
        // jour visé dans cette semaine. (wd - anchorWeekday) place dans la
        // semaine courante ; +7*weekOffset décale d'autant de semaines.
        const dayOffset = wd - anchorWeekday + weekOffset * 7;
        const startDate = zonedDatePlusDays(now, tz, dayOffset);
        const startMs = wallMinuteToUtc(startDate, block.startMinute, tz);
        const endDate = overnight
          ? zonedDatePlusDays(now, tz, dayOffset + 1)
          : startDate;
        const endMs = wallMinuteToUtc(endDate, block.endMinute, tz);
        out.push({ block, startMs, endMs });
      }
    }
  }
  out.sort((a, b) => a.startMs - b.startMs);
  return out;
}

/**
 * État du gate calculé depuis les blocs + l'instant courant. UI-facing.
 *
 *   - `inside`  : on est dans une fenêtre de shift (timestamp end fourni).
 *   - `next`    : prochaine fenêtre à venir (timestamp start fourni). Peut être
 *                 `null` (cas pathologique : aucun bloc dans les ~4 semaines).
 */
export interface ShiftWindowState {
  inside: ShiftInstance | null;
  next: ShiftInstance | null;
}

/**
 * Une absence prévenue (`absencesPrevenues`) : CE bloc, CE jour-là — la date
 * calendaire du DÉBUT de l'occurrence, dans le fuseau du bloc (« 2026-10-17 »).
 */
export interface AbsencePrevenue {
  shiftId: string;
  date: string;
}

/** La clé d'absence d'une occurrence : la date de son début, dans le fuseau
 *  du bloc — la même que celle que le contrôle des absences lit
 *  (`convex/_lib/absencesDuShift.ts`, `cleDeDate`). */
export function dateDeLOccurrence(inst: ShiftInstance): string {
  const d = calendarDateInZone(inst.startMs, fuseauDuBloc(inst.block.timezone));
  return `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
}

/**
 * Trouve « la fenêtre courante » et « la prochaine à venir » depuis `now`.
 *
 * Une occurrence couverte par une absence prévenue n'est JAMAIS « la
 * prochaine » : annoncer un shift que le chatteur a déclaré ne pas faire, c'est
 * l'écran qui ment (Président, 17-10-2026 : « jour off » enregistré, et
 * Personnel › Shift lui affichait toujours son shift du jour). En revanche elle
 * reste « la courante » : rien n'interdit de travailler un jour d'absence, le
 * serveur accepte le Start (`refusDeFenetre` ne lit pas les absences), et le
 * gate garde donc son bouton.
 */
export function computeShiftWindow(
  shifts: ShiftBlock[],
  now: number,
  absences: readonly AbsencePrevenue[] = [],
): ShiftWindowState {
  if (shifts.length === 0) return { inside: null, next: null };
  const excusees = new Set(absences.map((a) => `${a.shiftId}|${a.date}`));
  const instances = expandShiftInstances(shifts, now);
  let inside: ShiftInstance | null = null;
  let next: ShiftInstance | null = null;
  for (const inst of instances) {
    if (inst.startMs <= now && now < inst.endMs) {
      // Prendre la première instance trouvée : 2 blocs qui se chevauchent
      // est un cas-limite (le planning manager pourrait l'éviter). Si ça
      // arrive, on lock sur le premier dans l'ordre chronologique.
      if (!inside) inside = inst;
    } else if (inst.startMs > now) {
      if (excusees.has(`${inst.block._id}|${dateDeLOccurrence(inst)}`)) continue;
      if (next === null || inst.startMs < next.startMs) {
        next = inst;
      }
    }
  }
  return { inside, next };
}

// ─── État global du gate (rendu UI) ──────────────────────────────────────────

export type GateState =
  /** Caller bypass (ceo/manager/superadmin) — Conversations libre. */
  | { kind: "bypass" }
  /** L'org n'a aucun shift planifié — mode libre pour tous. */
  | { kind: "free" }
  /** L'org a des shifts mais le caller n'en a aucun — bloqué en permanence. */
  | { kind: "no-shifts-for-me" }
  /** Hors de toute fenêtre — bloqué jusqu'au prochain shift (ou indéfiniment
   *  si aucune fenêtre dans la portée 4 semaines). */
  | { kind: "outside"; next: ShiftInstance | null }
  /** Dans la fenêtre mais pas encore cliqué Start — overlay avec bouton. */
  | { kind: "pending-start"; window: ShiftInstance }
  /** Session active — panel libre, avec compteur restant côté UI. */
  | {
      kind: "active";
      window: ShiftInstance;
      sessionStartedAt: number;
      breakAllowanceMs: number;
      breakUsedMs: number;
      breakRemainingMs: number;
      breakStartedAt: number | null;
      /** Fin effective : min(scheduledEndMs de la session, endMs du bloc). On
       *  privilégie la session (autoritative serveur) si présente, sinon le
       *  bloc (fallback pour sessions legacy clockIn). */
      effectiveEndMs: number;
    };

export interface GateInput {
  bypass: boolean;
  orgHasShifts: boolean;
  myShifts: ShiftBlock[];
  /** Ses absences prévenues — sautées pour « la prochaine », jamais pour la
   *  courante (cf. `computeShiftWindow`). */
  absences?: readonly AbsencePrevenue[];
  activeSession: {
    _id: string;
    startedAt: number;
    scheduledEndMs: number | null;
    scheduledStartMs?: number | null;
    lastActivityAt?: number | null;
    breakUsedMs?: number | null;
    breakStartedAt?: number | null;
  } | null;
  now: number;
}

export function computeGateState(input: GateInput): GateState {
  if (input.bypass) return { kind: "bypass" };
  if (!input.orgHasShifts) return { kind: "free" };
  if (input.myShifts.length === 0) return { kind: "no-shifts-for-me" };

  const { inside, next } = computeShiftWindow(
    input.myShifts,
    input.now,
    input.absences ?? [],
  );

  // Cas 1 : session active côté serveur. Le bloc peut être passé (le user a
  // commencé en retard et est encore connecté après la fin) — on respecte la
  // session tant que le cron ne l'a pas fermée. effectiveEndMs = la borne dure
  // que la session porte (autoritative), sinon la fin de la fenêtre courante.
  if (input.activeSession) {
    const sessionEnd =
      input.activeSession.scheduledEndMs ?? (inside ? inside.endMs : null);
    if (sessionEnd && sessionEnd > input.now) {
      const sessionStart =
        input.activeSession.scheduledStartMs ?? inside?.startMs ?? input.activeSession.startedAt;
      const breakAllowanceMs = Math.max(0, Math.floor((sessionEnd - sessionStart) / 6));
      const explicitBreakMs =
        input.activeSession.breakStartedAt != null
          ? Math.max(0, input.now - input.activeSession.breakStartedAt)
          : 0;
      const idleMs =
        input.activeSession.breakStartedAt == null
          ? Math.max(
              0,
              input.now -
                (input.activeSession.lastActivityAt ?? input.activeSession.startedAt),
            )
          : 0;
      const pendingIdleBreakMs = idleMs >= 3 * 60 * 1000 ? idleMs : 0;
      const breakUsedMs =
        (input.activeSession.breakUsedMs ?? 0) +
        explicitBreakMs +
        pendingIdleBreakMs;
      return {
        kind: "active",
        // Sans `inside`, on synthétise un wrapper minimal autour de la session
        // pour que l'UI compteur sache d'où partir.
        window: inside ?? {
          // Bloc placeholder — l'UI lit `effectiveEndMs`, pas `window.block`.
          block: input.myShifts[0]!,
          startMs: input.activeSession.startedAt,
          endMs: sessionEnd,
        },
        sessionStartedAt: input.activeSession.startedAt,
        breakAllowanceMs,
        breakUsedMs,
        breakRemainingMs: Math.max(0, breakAllowanceMs - breakUsedMs),
        breakStartedAt: input.activeSession.breakStartedAt ?? null,
        effectiveEndMs: sessionEnd,
      };
    }
    // Session active mais déjà périmée (cron pas encore passé) → on retombe
    // sur le calcul normal ci-dessous (la session sera fermée d'ici 60s).
  }

  if (inside) return { kind: "pending-start", window: inside };
  return { kind: "outside", next };
}

/** True si l'état doit afficher l'overlay grisé (vs panneau libre). */
export function isGateBlocking(state: GateState | null): boolean {
  if (!state) return false;
  return (
    state.kind === "outside" ||
    state.kind === "no-shifts-for-me" ||
    state.kind === "pending-start"
  );
}

/**
 * True UNIQUEMENT pour les états où l'accès aux Conversations est EXPLICITEMENT
 * autorisé : superviseur (`bypass`), org sans shift (`free`), ou session de
 * shift en cours (`active`). Tout le reste — y compris `null` (gate en cours de
 * chargement / compte non résolu) — n'est PAS autorisé.
 *
 * Le panneau s'appuie sur CE helper en mode « fail-closed » : on ne montre les
 * conversations que si l'accès est prouvé. Sinon (état bloquant CONNU OU état
 * inconnu/chargement) on couvre le panneau. Évite la faille où un chatter sans
 * shift accédait librement parce que la query n'avait pas encore répondu (ou
 * que son compte actif n'était pas résolu) → `state` null → aucun overlay.
 */
export function isGateAllowing(state: GateState | null): boolean {
  if (!state) return false;
  return (
    state.kind === "bypass" ||
    state.kind === "free" ||
    state.kind === "active"
  );
}

// ─── Formatters (compteurs) ──────────────────────────────────────────────────

/**
 * « 1h 23m » / « 23m » / « < 1m ». Pas de secondes (l'utilisateur n'a pas
 * besoin de précision seconde sur un timer de plusieurs minutes / heures).
 */
export function formatDurationCoarse(ms: number): string {
  const safe = Math.max(0, ms);
  const totalMin = Math.floor(safe / 60_000);
  if (totalMin < 1) return "< 1 min";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/**
 * « 09:42 » — l'heure d'un instant de shift.
 *
 * ⛔ `tz` est le fuseau du SHIFT (`fuseauDuBloc(block.timezone)`), jamais
 * `useDisplayTimezone()`. C'est ce qui fait dire au cockpit le même nombre que
 * le salon vocal Discord ; l'inverse a coûté deux heures de shift par jour au
 * titulaire du Shift A, dont le poste affiche UTC (cf. `FUSEAU_DES_SHIFTS`).
 * Le nom précédent — `formatLocalTime` — nommait très exactement l'erreur.
 */
export function formatHeureDeShift(
  ts: number,
  tz: string,
  locale = "fr",
): string {
  return formatTimeInZone(ts, tz, locale);
}

/** Différence en jours calendaires (dans `tz`) entre deux instants. */
function calendarDayDiff(a: number, b: number, tz: string): number {
  const da = calendarDateInZone(a, tz);
  const db = calendarDateInZone(b, tz);
  const ma = Date.UTC(da.year, da.month - 1, da.day);
  const mb = Date.UTC(db.year, db.month - 1, db.day);
  return Math.round((ma - mb) / DAY_MS);
}

/**
 * Étiquette d'un jour pour annoncer une occurrence à venir, calculée dans le
 * fuseau du SHIFT (même règle que `formatHeureDeShift`) :
 *   - aujourd'hui → « aujourd'hui »
 *   - demain      → « demain »
 *   - sinon       → libellé long localisé (« lundi »)
 */
export function relativeDayLabel(
  ts: number,
  now: number,
  tz: string,
  locale = "fr",
): string {
  const diffDays = calendarDayDiff(ts, now, tz);
  if (diffDays === 0) return locale === "fr" ? "aujourd'hui" : "today";
  if (diffDays === 1) return locale === "fr" ? "demain" : "tomorrow";
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    timeZone: tz,
  }).format(new Date(ts));
}
